'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';
import { EASE_DEFAULT_MOTION, EASE_EXIT_MOTION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
export interface GeneratingFrameProps {
  /** 'circle' for avatars, 'square' for hero / launchpad cover. */
  shape: 'circle' | 'square';
  /** Expected duration in ms — drives the 0→90% sweep timing. Snap to 100% on done. */
  expectedDurationMs: number;
  /** Set to true when the underlying mutation resolves; triggers the snap-to-100% + fade. */
  done: boolean;
  /** Whatever the parent wants inside the frame — typically <ImageShimmer />. */
  children: ReactNode;
  /** Optional className passthrough for size/positioning. */
  className?: string;
}

// SVG geometry — kept module-scope so React Compiler can dedupe and Motion
// variants stay referentially stable. The viewBox is square for both shapes;
// width/height come from the parent container via absolute inset-0.
const SVG_SIZE: number = 100;
const STROKE_WIDTH: number = 3;
// Inset by half the stroke so the painted line stays inside the box.
const HALF_STROKE: number = STROKE_WIDTH / 2;
const CIRCLE_R: number = SVG_SIZE / 2 - HALF_STROKE;
const CIRCLE_CX: number = SVG_SIZE / 2;
const CIRCLE_CY: number = SVG_SIZE / 2;
const CIRCLE_CIRCUMFERENCE: number = 2 * Math.PI * CIRCLE_R;

// Square geometry — rounded corners 12px to match `--radius-card` on a 100px viewBox.
// (Shape is rendered into the parent box via `preserveAspectRatio="none"` so the
// 12 here is a relative arc-radius, which scales with the box; close enough to
// the rounded-card token at the typical 200–600px render sizes.)
const RECT_X: number = HALF_STROKE;
const RECT_Y: number = HALF_STROKE;
const RECT_W: number = SVG_SIZE - STROKE_WIDTH;
const RECT_H: number = SVG_SIZE - STROKE_WIDTH;
const RECT_RX: number = 12;
// Rect perimeter — for a rounded rect: 2*(w + h) - 8r + 2*pi*r (corners replace 4 right-angle
// corners with arcs of radius r). Sufficiently accurate for stroke-dash timing.
const RECT_PERIMETER: number = 2 * (RECT_W + RECT_H) - 8 * RECT_RX + 2 * Math.PI * RECT_RX;

// 90% of the path — we sweep to 90% over `expectedDurationMs`, then snap to 100%
// when `done` arrives. The 10% buffer covers normal-case latency overruns so the
// bar doesn't sit at "100%" while the result is still loading.
const SWEEP_FRACTION: number = 0.9;

// Snap-to-100% duration on completion + fade-out duration.
const SNAP_MS: number = 200;
const FADE_MS: number = 300;
const SNAP_TRANSITION: {
  readonly duration: number;
  readonly ease: typeof EASE_DEFAULT_MOTION;
} = {
  duration: SNAP_MS / 1000,
  ease: EASE_DEFAULT_MOTION
} as const;
const FADE_EXIT: {
  readonly duration: number;
  readonly ease: typeof EASE_EXIT_MOTION;
} = {
  duration: FADE_MS / 1000,
  ease: EASE_EXIT_MOTION
} as const;

/**
 * `<GeneratingFrame>` — animated progress border around a generating preview.
 *
 * Wraps an `<ImageShimmer />` (or any other child) with an SVG stroke that
 * sweeps from 0% → 90% over `expectedDurationMs`, then snaps to 100% + fades
 * when the parent flips `done` to true. Used as the visual scaffold around
 * Morfian (text→image) and Morfianate (img→img) generation.
 *
 * Honours `useReducedMotion`: under reduced motion, the stroke renders at
 * 100% immediately when `done`, otherwise it shows the dash with no animation.
 */
export function GeneratingFrame(t0) {
  const $ = _c(36);
  const {
    shape,
    expectedDurationMs,
    done,
    children,
    className
  } = t0;
  const prefersReduced = useReducedMotion();
  const isCircle = shape === "circle";
  const totalLength = isCircle ? CIRCLE_CIRCUMFERENCE : RECT_PERIMETER;
  const sweepOffset = totalLength * (1 - SWEEP_FRACTION);
  const sweepDurationS = expectedDurationMs / 1000;
  let t1;
  if ($[0] !== prefersReduced || $[1] !== totalLength) {
    t1 = prefersReduced ? false : {
      strokeDashoffset: totalLength
    };
    $[0] = prefersReduced;
    $[1] = totalLength;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sweepInitial = t1;
  let t2;
  if ($[3] !== done || $[4] !== prefersReduced || $[5] !== sweepOffset || $[6] !== totalLength) {
    t2 = done ? {
      strokeDashoffset: 0
    } : prefersReduced ? {
      strokeDashoffset: totalLength
    } : {
      strokeDashoffset: sweepOffset
    };
    $[3] = done;
    $[4] = prefersReduced;
    $[5] = sweepOffset;
    $[6] = totalLength;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const sweepAnimate = t2;
  let t3;
  if ($[8] !== done || $[9] !== prefersReduced || $[10] !== sweepDurationS) {
    t3 = done ? SNAP_TRANSITION : prefersReduced ? {
      duration: 0
    } : {
      duration: sweepDurationS,
      ease: EASE_DEFAULT_MOTION
    };
    $[8] = done;
    $[9] = prefersReduced;
    $[10] = sweepDurationS;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const sweepTransition = t3;
  let t4;
  if ($[12] !== prefersReduced) {
    t4 = prefersReduced ? false : {
      opacity: 1
    };
    $[12] = prefersReduced;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const fadeInitial = t4;
  const t5 = done ? 0 : 1;
  let t6;
  if ($[14] !== t5) {
    t6 = {
      opacity: t5
    };
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const fadeAnimate = t6;
  let t7;
  if ($[16] !== done || $[17] !== prefersReduced) {
    t7 = done ? prefersReduced ? {
      duration: 0,
      delay: SNAP_MS / 1000
    } : {
      ...FADE_EXIT,
      delay: SNAP_MS / 1000
    } : {
      duration: 0
    };
    $[16] = done;
    $[17] = prefersReduced;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const fadeTransition = t7;
  let t8;
  if ($[19] !== className) {
    t8 = cn("relative size-full", className);
    $[19] = className;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== isCircle || $[22] !== sweepAnimate || $[23] !== sweepInitial || $[24] !== sweepTransition || $[25] !== totalLength) {
    t9 = isCircle ? <m.circle cx={CIRCLE_CX} cy={CIRCLE_CY} r={CIRCLE_R} fill="none" stroke="var(--brand-700)" strokeWidth={STROKE_WIDTH} strokeLinecap="round" strokeDasharray={totalLength} transform={`rotate(-90 ${String(CIRCLE_CX)} ${String(CIRCLE_CY)})`} initial={sweepInitial} animate={sweepAnimate} transition={sweepTransition} /> : <m.rect x={RECT_X} y={RECT_Y} width={RECT_W} height={RECT_H} rx={RECT_RX} ry={RECT_RX} fill="none" stroke="var(--brand-700)" strokeWidth={STROKE_WIDTH} strokeLinecap="round" strokeDasharray={totalLength} initial={sweepInitial} animate={sweepAnimate} transition={sweepTransition} />;
    $[21] = isCircle;
    $[22] = sweepAnimate;
    $[23] = sweepInitial;
    $[24] = sweepTransition;
    $[25] = totalLength;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== fadeAnimate || $[28] !== fadeInitial || $[29] !== fadeTransition || $[30] !== t9) {
    t10 = <LazyMotion features={domAnimation}><m.svg aria-hidden="true" viewBox={`0 0 ${String(SVG_SIZE)} ${String(SVG_SIZE)}`} preserveAspectRatio="none" className="pointer-events-none absolute inset-0 size-full overflow-visible" initial={fadeInitial} animate={fadeAnimate} transition={fadeTransition}>{t9}</m.svg></LazyMotion>;
    $[27] = fadeAnimate;
    $[28] = fadeInitial;
    $[29] = fadeTransition;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== children || $[33] !== t10 || $[34] !== t8) {
    t11 = <div className={t8}>{children}{t10}</div>;
    $[32] = children;
    $[33] = t10;
    $[34] = t8;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}